import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import type { UserDocument, CreateUserDocumentInput, UpdateUserDocumentInput } from '@/types/userDocument';

const BUCKET = 'user-documents';

export const userDocumentService = {
  async getDocuments(userId: string): Promise<UserDocument[]> {
    const tenantId = await getCurrentTenantId();
    const { data, error } = await supabase
      .from('user_documents')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async uploadDocument(input: CreateUserDocumentInput): Promise<UserDocument> {
    const tenantId = await getCurrentTenantId();
    const { data: { user } } = await supabase.auth.getUser();

    // Upload file to storage
    const fileExt = input.file.name.split('.').pop();
    const storagePath = `${tenantId}/${input.user_id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, input.file);

    if (uploadError) throw uploadError;

    // Create database record
    const { data, error } = await supabase
      .from('user_documents')
      .insert({
        user_id: input.user_id,
        tenant_id: tenantId,
        document_name: input.document_name,
        document_type: input.document_type || null,
        storage_path: storagePath,
        file_name: input.file.name,
        file_type: input.file.type,
        file_size_bytes: input.file.size,
        expires_at: input.expires_at || null,
        notes: input.notes || null,
        uploaded_by_user_id: user?.id || null,
      })
      .select()
      .single();

    if (error) {
      // Clean up uploaded file if DB insert fails
      await supabase.storage.from(BUCKET).remove([storagePath]);
      throw error;
    }

    return data;
  },

  async updateDocument(id: string, updates: UpdateUserDocumentInput): Promise<UserDocument> {
    const { data, error } = await supabase
      .from('user_documents')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteDocument(doc: UserDocument): Promise<void> {
    // Delete file from storage
    await supabase.storage.from(BUCKET).remove([doc.storage_path]);

    // Delete database record
    const { error } = await supabase
      .from('user_documents')
      .delete()
      .eq('id', doc.id);

    if (error) throw error;
  },

  async getDownloadUrl(storagePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(storagePath, 3600); // 1 hour

    if (error) throw error;
    return data.signedUrl;
  },

  async getUsedDocumentTypes(): Promise<string[]> {
    const tenantId = await getCurrentTenantId();
    const { data, error } = await supabase
      .from('user_documents')
      .select('document_type')
      .eq('tenant_id', tenantId)
      .not('document_type', 'is', null);

    if (error) throw error;

    const types = Array.from(
      new Set((data || []).map(d => d.document_type).filter((t): t is string => !!t))
    ).sort();
    return types;
  },
};
